import React from 'react';
import {Box, Typography, Grid, Tooltip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import NationalDashboardShell from '../components/NationalDashboard.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {READINESS_BAND_COLORS, CONTEXT_BAND_COLORS, RISK_BAND_COLORS, BAND_COLOR} from '../constants';

import { useDashboard } from '../../../context/DashboardContext';
import {isSuppressedItem, isSuppressedKpi, SUPPRESSED_COPY} from '../../DashboardCommon/dashboardCards';
import {SuppressedValue} from '../../DashboardCommon/SuppressedValue.comp';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';

import KpiCard from '../components/KpiCard.comp';
import SectionCard from '../components/SectionCard.comp';
import MetaInfoBar from '../components/MetaInfoBar.comp';

import {BandChip as SharedBandChip} from '../../DashboardCommon/widgets.comp';
import {UNIT, fmtValue, benchmarkText} from '../../DashboardCommon/units';
const NAVY   = '#052F44';
const ORANGE = '#FF6900';

// Static copy (frontend-owned): the S_3 TitleCard is no longer emitted.
const SUMMARY_TITLE = "National Baseline Overview";
const SUMMARY_SUBTITLE = "How is the country doing overall?";

const SectionHeader = ({title, subtitle}) => (
    <Box sx={{mb: 3}}>
        <Typography sx={{fontSize: '23px', fontWeight: 800, color: NAVY, lineHeight: 1.25}}>
            {title}
        </Typography>
        {subtitle && (
            <Typography sx={{fontSize: '14px', fontStyle: 'italic', color: '#6B7280', mt: 0.75, lineHeight: 1.6}}>
                {subtitle}
            </Typography>
        )}
    </Box>
);
// Secondary benchmark-marker colour (Global) — kept distinct from NAVY and
// from every tier/band fill colour used on the bars themselves.
const MARKER_SECONDARY = 'var(--brand-global-2, #7B8FC7)';

/* ── Risk level helpers (separate from tier colours) ───────────── */
const RISK_LEVEL_COLOR = {
    Low:      '#A5A5A5',
    Medium:   '#7B8FC7',
    High:     ORANGE,
    Critical: '#B91C6C',
    'Priority Concern': '#E04535',
    Elevated: '#E8730C',
    Watch:    '#EFA020',
    Monitor:  '#4A9278',
};

/* ── Target score progress bar ──────────────────────────────────── */
// An area under the n floor (suppressed) keeps its label and shows the placeholder in place of bar, score and band.
const TargetScoreBar = ({label, score, tier, benchmark, suppressed, bandColorMap = READINESS_BAND_COLORS}) => {
    const tierColor = bandColorMap[tier] || BAND_COLOR[tier] || ORANGE;
    if (suppressed) {
        return <Box sx={{mb: 2.5}}><SuppressedValue label={label}/></Box>;
    }
    return (
        <Box sx={{mb: 2.5}}>
            <Typography sx={{fontSize: '12px', color: '#374151', mb: 1, lineHeight: 1.4}}>
                {label}
            </Typography>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                {/* Track — no overflow:hidden so the benchmark notch can extend beyond */}
                <Box sx={{
                    flex: 1,
                    height: '12px',
                    backgroundColor: 'var(--brand-surface-muted, #F3F4F6)',
                    borderRadius: '6px',
                    position: 'relative',
                }}>
                    {/* Country fill */}
                    <Tooltip title={fmtValue(score)} arrow placement="top">
                        <Box sx={{
                            position: 'absolute',
                            left: 0, top: 0, bottom: 0,
                            width: `${score ?? 0}%`,
                            backgroundColor: tierColor,
                            borderRadius: '6px',
                            overflow: 'hidden',
                            cursor: 'pointer',
                        }}/>
                    </Tooltip>
                    {/* Global avg marker — hollow upward-pointing triangle below the bar */}
                    {benchmark != null && (
                        <Tooltip title={benchmarkText('Global avg', benchmark)} arrow placement="bottom">
                            <Box sx={{
                                position: 'absolute',
                                left: `${benchmark}%`,
                                bottom: '-9px',
                                width: 0,
                                height: 0,
                                borderLeft: '6px solid transparent',
                                borderRight: '6px solid transparent',
                                borderBottom: `9px solid ${MARKER_SECONDARY}`,
                                transform: 'translateX(-50%)',
                                cursor: 'pointer',
                                zIndex: 4,
                            }}/>
                        </Tooltip>
                    )}
                </Box>
                {/* Score */}
                <Typography sx={{
                    fontSize: '13px',
                    fontWeight: 700,
                    color: '#111827',
                    minWidth: '24px',
                    textAlign: 'right',
                }}>
                    {score ?? '—'}
                </Typography>
                {/* Tier badge */}
                {tier && <SharedBandChip label={tier} color={tierColor}/>}
            </Box>
        </Box>
    );
};

/* ── Risk horizontal bar ────────────────────────────────────────── */
// A risk under the n floor (suppressed) keeps its label and shows the placeholder in place of bar, figure and band.
const RiskBar = ({label, percent, band, benchmark, suppressed}) => (suppressed ? <Box sx={{mb: 2}}><SuppressedValue label={label}/></Box> : (
    <Box sx={{mb: 2}}>
        <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5, gap: 1}}>
            <Typography sx={{fontSize: '13px', color: '#374151', flex: 1}}>
                {label}
            </Typography>
            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0}}>
                <Typography sx={{fontSize: '13px', fontWeight: 700, color: '#374151'}}>
                    {percent === null ? '—' : `${percent}%`}
                </Typography>
                {band && <SharedBandChip label={band} color={RISK_BAND_COLORS[band] || '#9CA3AF'}/>}
            </Box>
        </Box>
        {/* Track — no overflow:hidden so the Global avg marker can extend beyond */}
        <Box sx={{height: '12px', backgroundColor: 'var(--brand-surface-muted, #F3F4F6)', borderRadius: '6px', position: 'relative'}}>
            <Tooltip title={percent === null ? '—' : `${percent}%`} arrow placement="top">
                <Box sx={{
                    position: 'absolute', left: 0, top: 0, bottom: 0,
                    width: `${percent ?? 0}%`,
                    backgroundColor: RISK_BAND_COLORS[band] || '#9CA3AF',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                }}/>
            </Tooltip>
            {/* Global avg marker — hollow upward-pointing triangle below the bar */}
            {benchmark != null && (
                <Tooltip title={benchmarkText('Global avg', benchmark, UNIT.PERCENT)} arrow placement="bottom">
                    <Box sx={{
                        position: 'absolute',
                        left: `${benchmark}%`,
                        bottom: '-9px',
                        width: 0,
                        height: 0,
                        borderLeft: '6px solid transparent',
                        borderRight: '6px solid transparent',
                        borderBottom: `9px solid ${MARKER_SECONDARY}`,
                        transform: 'translateX(-50%)',
                        cursor: 'pointer',
                        zIndex: 4,
                    }}/>
                </Tooltip>
            )}
        </Box>
    </Box>
));

/* ── Regional attention table ───────────────────────────────────── */
// A MUI table (was a CSS grid) so every column sorts and filters from its header (QA 9 Oct 2026);
// the look is kept — navy 12px header, 13px zebra rows, risk 55px / level 105px.
const regionalHeadCell = {backgroundColor: NAVY, color: '#ffffff', fontSize: '12px', fontWeight: 700, borderBottom: 'none', py: 1, px: 1.5};
const regionalBodyCell = {fontSize: '13px', color: '#374151', py: 1.2, px: 1.5, borderBottom: 'none'};
const REGIONAL_COLUMNS = [
    {key: 'region', label: 'Region', type: 'text'},
    {key: 'risk', label: 'Risk', type: 'percent', accessor: (r) => (r.suppressed ? null : r.risk)},
    {key: 'level', label: 'Level', type: 'band', order: ['Monitor', 'Watch', 'Elevated', 'Priority Concern'], accessor: (r) => r.level},
];

const RegionalTable = ({rows, emptyText}) => {
    const t = useTableControls(rows, REGIONAL_COLUMNS);
    return (
        <Box sx={{width: '100%'}}>
            {(t.chips.length > 0 || rows.length >= 5) && <TableToolbar {...t} sx={{mb: 1}}/>}
            <TableContainer sx={{borderRadius: '8px', overflow: 'hidden'}}>
                <Table size="small" sx={{tableLayout: 'fixed'}}>
                    <TableHead>
                        <TableRow>
                            <SortFilterHeaderCell {...t.headerProps('region')} sx={{...regionalHeadCell, textAlign: 'left'}}/>
                            <SortFilterHeaderCell {...t.headerProps('risk')} sx={{...regionalHeadCell, textAlign: 'center', width: 55}}/>
                            <SortFilterHeaderCell {...t.headerProps('level')} sx={{...regionalHeadCell, textAlign: 'center', width: 105}}/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {emptyText && rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={3} sx={{...regionalBodyCell, color: '#6A7282', textAlign: 'center', py: 2}}>{emptyText}</TableCell>
                            </TableRow>
                        )}
                        {rows.length > 0 && t.rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={3} sx={{...regionalBodyCell, color: '#6A7282', textAlign: 'center', py: 2}}>No region matches the filters.</TableCell>
                            </TableRow>
                        )}
                        {t.rows.map((row, idx) => (
                            <TableRow key={row.region} sx={{backgroundColor: idx % 2 === 0 ? 'var(--brand-surface, #ffffff)' : 'var(--brand-surface-subtle, #F9FAFB)'}}>
                                <TableCell sx={{...regionalBodyCell, textAlign: 'left'}}>{row.region}</TableCell>
                                <TableCell sx={{...regionalBodyCell, fontWeight: 600, textAlign: 'center'}}>{row.suppressed ? <SuppressedValue variant="cell"/> : row.risk}</TableCell>
                                <TableCell sx={{...regionalBodyCell, textAlign: 'center'}}>
                                    {row.level && <SharedBandChip label={row.level} color={RISK_LEVEL_COLOR[row.level] || BAND_COLOR[row.level] || READINESS_BAND_COLORS[row.level] || '#A5A5A5'}/>}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

/* ── Executive Summary Section ──────────────────────────────────── */
const NATIONAL_SUMMARY_SECTION_CODE = 'S_3';
const CARD_SECTIONS = [NATIONAL_SUMMARY_SECTION_CODE];

const ExecutiveSummarySection = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTIONS });

    // Defensively locate section S_3
    let s3Section = null;
    if (dashboardData) {
        if (dashboardData.section_code === NATIONAL_SUMMARY_SECTION_CODE) {
            s3Section = dashboardData;
        } else if (dashboardData.cards?.sections) {
            s3Section = dashboardData.cards.sections.find(s => s.section_code === NATIONAL_SUMMARY_SECTION_CODE);
        } else if (Array.isArray(dashboardData.sections)) {
            s3Section = dashboardData.sections.find(s => s.section_code === NATIONAL_SUMMARY_SECTION_CODE);
        } else if (dashboardData.cards && Array.isArray(dashboardData.cards)) {
            s3Section = dashboardData;
        }
    }

    if (loading) {
        return (
            <NationalDashboardShell>
                <CircularLoader />
            </NationalDashboardShell>
        );
    }

    if (!dashboardData || !s3Section) {
        return (
            <NationalDashboardShell>
                <NoDataAvailable />
            </NationalDashboardShell>
        );
    }

    const s3Cards = s3Section?.cards || [];

    // 1. Meta Items
    const orgCard = s3Cards.find(c => c.card_type === 'ORGCard');
    const metaItems = (orgCard?.table_objects || []).map(obj => {
        let label = obj.column_name;
        label = label.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
        const value = obj.values && obj.values[0] ? obj.values[0] : '';

        return { label, value };
    });

    // 2. KPI Data
    const kpiCards = s3Cards.filter(c => c.card_type === 'KPICard');
    const kpiData = kpiCards.map(c => {
        let label = c.kpi_name.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
        // Truncate decimals from the KPI figure — "63.69" → "63", "92.86%" → "92%"
        // (approved fix: truncate all decimal points across the summaries).
        // A KPI under the n floor (suppressed) reads "—" with the reason underneath and no band;
        // a value that is not sent reads "—" (never the string "null").
        const suppressed = isSuppressedKpi(c);
        const kpiNum = Number(c.kpi_value);
        const hasValue = !suppressed && c.kpi_value !== null && c.kpi_value !== undefined && c.kpi_value !== '';
        let value = !hasValue ? '—' : (Number.isFinite(kpiNum) ? String(Math.trunc(kpiNum)) : String(c.kpi_value));
        const isRiskKpi = c.kpi_name.toUpperCase().includes('RISK') || c.kpi_name.toUpperCase().includes('EXPERIENCE');
        if (hasValue && (c.kpi_name.toUpperCase().includes('PARTICIPATION') || isRiskKpi)) {
            value = value + '%';
        }
        if (suppressed) {
            return {label, value, subValue: SUPPRESSED_COPY, tier: '', tierColor: '#D1D5DB', topColor: '#D1D5DB'};
        }

        let subValue = undefined;
        const infos = c.kpi_add_infos || [];
        const isBench = (info) => /avg\.?$/i.test(String(info.info_name || '').trim());
        const benches = infos.filter(info => isBench(info) && info.info_value != null);
        if (infos.length >= 2 && !benches.length) {
            const p = infos.find(info => info.info_name.toLowerCase().includes('participant') || info.info_name.toLowerCase().includes('n'))?.info_value;
            const r = infos.find(info => info.info_name.toLowerCase().includes('registered') || info.info_name.toLowerCase().includes('total'))?.info_value;
            if (p != null && r != null) {
                subValue = `${p.toLocaleString()} / ${r.toLocaleString()}`;
            } else {
                subValue = infos.map(info => `${info.info_name}: ${info.info_value}`).join(' | ');
            }
        } else if (benches.length) {
            // Benchmark add-infos — the datahouse sends the real values ("Global avg." 54, and
            // "National avg." where a dashboard has one). The deck's "Global avg XX" placeholder
            // used to sit here because this branch only looked at pairs of add-infos.
            const fmt = (v) => {
                const n = Number(v);
                return (Number.isFinite(n) ? String(Math.trunc(n)) : String(v)) + (isRiskKpi ? '%' : '');
            };
            subValue = (
                <>
                    {benches.map((info, i) => (
                        <React.Fragment key={info.info_name}>
                            {i > 0 ? ' · ' : ''}{String(info.info_name).replace(/\.$/, '')} <strong>{fmt(info.info_value)}</strong>
                        </React.Fragment>
                    ))}
                </>
            );
        }
        
        const tier = c.kpi_band || '';
        const tierColor = isRiskKpi 
            ? (RISK_LEVEL_COLOR[tier] || RISK_BAND_COLORS[tier] || BAND_COLOR[tier] || ORANGE)
            : (BAND_COLOR[tier] || CONTEXT_BAND_COLORS[tier] || READINESS_BAND_COLORS[tier] || ORANGE);

        return {
            label,
            value,
            subValue,
            tier,
            tierColor,
            topColor: tierColor,
        };
    });

    // 3. Area Scores (top 3)
    const areaScoresCard = s3Cards.find(c => c.card_code === 'C_6' || (c.card_type === 'BarChartBMCard' && c.card_code !== 'C_6a' && c.card_code !== 'C_6A'));
    // An item under the n floor arrives flagged (a ranked list normally leaves it out): no score, band or bar.
    const areaScores = (areaScoresCard?.items || []).slice(0, 3).map(item => {
        const globalBenchmark = item.benchmark_values?.find(b => b.name === 'Global avg.')?.value;
        const suppressed = isSuppressedItem(item);
        return {
            label: item.label,
            suppressed,
            score: !suppressed && item.value != null ? Math.trunc(item.value) : null,
            tier: suppressed ? null : item.band,
            benchmark: globalBenchmark ? Math.trunc(globalBenchmark) : null
        };
    });

    // Extract dynamic Context & Support scores via card_code = C_6a (top 3)
    const c6aCard = s3Cards.find(c => c.card_code === 'C_6a' || c.card_code === 'C_6A');
    const c6aTitle = c6aCard?.title;
    const contextScores = (c6aCard?.items || []).slice(0, 3).map(item => {
        const globalBenchmark = item.benchmark_values?.find(b => b.name === 'Global avg.')?.value || item.benchmark_values?.find(b => b.name?.includes('Global'))?.value;
        const suppressed = isSuppressedItem(item);
        return {
            label: item.label,
            suppressed,
            score: !suppressed && item.value != null ? Math.trunc(item.value) : null,
            tier: suppressed ? null : item.band,
            benchmark: globalBenchmark ? Math.trunc(globalBenchmark) : null
        };
    });

    // 4. Top Cyber Risks (top 5)
    const cyberRisksCard = s3Cards.find(c => c.card_code === 'C_7');
    const cyberRisks = (cyberRisksCard?.items || []).slice(0, 5).map(item => {
        const globalBenchmark = item.benchmark_values?.find(b => b.name === 'Global avg.')?.value;
        const suppressed = isSuppressedItem(item);
        return {
            label: item.label,
            suppressed,
            percent: !suppressed && item.value != null ? Math.trunc(item.value) : null,
            band: suppressed ? null : item.band,
            benchmark: globalBenchmark != null ? Math.trunc(globalBenchmark) : null
        };
    });

    // 5. Regions to Watch
    const regionalCard = s3Cards.find(c => c.card_code === 'C_8');
    // A region under the n floor arrives flagged (the datahouse normally leaves it out of this ranking):
    // placeholder, no level. A card with no row left says so.
    const regionalData = (regionalCard?.items || []).map(item => {
        const suppressed = isSuppressedItem(item);
        return {
            region: item.label,
            suppressed,
            risk: !suppressed && item.value != null ? `${Math.trunc(item.value)}%` : '—',
            level: suppressed ? null : item.band
        };
    });
    const regionalEmptyText = regionalCard && regionalData.length === 0 ? 'No regions with 10 or more students to rank.' : null;

    return (
        <NationalDashboardShell>
            <SectionHeader title={SUMMARY_TITLE} subtitle={SUMMARY_SUBTITLE}/>

            {/* Metadata bar */}
            <MetaInfoBar items={metaItems}/>

            {/* KPI cards */}
            <Grid container spacing={2.5} sx={{mb: 3}}>
                {kpiData.map(kpi => (
                    <Grid item xs={12} md={12 / kpiData.length} key={kpi.label}>
                        <KpiCard {...kpi}/>
                    </Grid>
                ))}
            </Grid>

            {/* Lower three sections */}
            <Grid container spacing={2.5}>
                {/* Area Scores */}
                <Grid item xs={12} md={4}>
                    <SectionCard
                        title={areaScoresCard?.title || "Readiness Areas at a Glance"}
                        titleRight={
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.8}}>
                                <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: `7px solid ${MARKER_SECONDARY}`}}/>
                                <Typography sx={{fontSize: '12px', color: '#6A7282'}}>Global avg</Typography>
                            </Box>
                        }
                    >
                        {areaScores.map(t => (
                            <TargetScoreBar key={t.label} {...t}/>
                        ))}
                        {contextScores.length > 0 && (
                            <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid var(--brand-border, #E5E7EB)' }}>
                                <Typography sx={{ fontSize: '16px', fontWeight: 700, color: NAVY, mb: 2.5 }}>
                                    {c6aTitle}
                                </Typography>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2.5}}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.8}}>
                                        <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: `7px solid ${MARKER_SECONDARY}`}}/>
                                        <Typography sx={{fontSize: '12px', color: '#6A7282'}}>Global avg</Typography>
                                    </Box>
                                </Box>
                                {contextScores.map(t => (
                                    <TargetScoreBar key={t.label} {...t} bandColorMap={CONTEXT_BAND_COLORS}/>
                                ))}
                            </Box>
                        )}
                    </SectionCard>
                </Grid>

                {/* Top Cyber Risks */}
                <Grid item xs={12} md={4}>
                    <SectionCard
                        title="Top Child Cyber Risks"
                        linkTo="/dq_pro/national-dashboard/cyber-risks"
                        titleRight={
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.8}}>
                                <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: `7px solid ${MARKER_SECONDARY}`}}/>
                                <Typography sx={{fontSize: '12px', color: '#6A7282'}}>Global avg</Typography>
                            </Box>
                        }
                    >
                        {cyberRisks.map(r => (
                            <RiskBar key={r.label} {...r}/>
                        ))}
                    </SectionCard>
                </Grid>

                {/* Regional Attention */}
                <Grid item xs={12} md={4}>
                    <SectionCard title={regionalCard?.title || "Regions to Watch"}>
                        <RegionalTable rows={regionalData} emptyText={regionalEmptyText}/>
                    </SectionCard>
                </Grid>
            </Grid>
        </NationalDashboardShell>
    );
};

export default ExecutiveSummarySection;
